import * as React from 'react';

import { IDisplayValue } from '../../ts/Interfaces';
import { CLEAR_SELECTIONS_VALUE, IMatch, SHOW_MORE_VALUE, ShowMoreElement, TitleOfMatch } from './Buttons';
import * as Globalization from '../../../../_common/platform/ts/Globalization';
import { IValuesCollection } from '../../ts/appTheme';
import { themeVars } from '../../../../pdc-platform/src/ts/themes/themeConstants';
import { ITreeNode } from '../../../../pdc-platform/src/ts/Interfaces';

export interface IExpandedStates {
    [id: string]: boolean;
}

export interface IItemReferences {
    [value: string]: HTMLDivElement;
}

export interface ITreeSearchNode extends ITreeNode, IMatch {}

export interface ITreeItem extends IDisplayValue {
    key: string;
}

function TogglerCell(props: {
    collapsed: boolean;
    showToggler: boolean;
    onToggle(e: React.MouseEvent<HTMLTableCellElement, MouseEvent>): void;
}) {
    let toggler: React.JSX.Element;

    if (props.showToggler) {
        toggler = <i className={'fa toggle' + (props.collapsed ? ' fa-chevron-right' : ' fa-chevron-down')}></i>;
    } else {
        toggler = <span className="empty-toggler"></span>;
    }

    return (
        <td className="toggler-cell" onClick={props.showToggler ? props.onToggle : undefined}>
            <div className="toggler-cell-content">{toggler}</div>
        </td>
    );
}

export function findNodeIndexByKey(key: string, nodes: ITreeItem[]) {
    if (nodes == null) {
        return undefined;
    }

    return nodes.findIndex((node) => {
        return node.key === key;
    });
}

export function findNodeIndexByValue(value: string, nodes: ITreeItem[]) {
    if (nodes == null) {
        return undefined;
    }

    return nodes.findIndex((node) => {
        return node.value === value;
    });
}

export function findNodesByID(id: string, nodes: ITreeNode[], result: ITreeNode[] = []) {
    nodes?.forEach((node) => {
        if (node.id === id) {
            result.push(node);
        }

        findNodesByID(id, node.subNodes, result);
    });

    return result;
}

export function findNodeIndex(key: string, value: string, nodes: ITreeItem[]) {
    if (nodes == null) {
        return undefined;
    }

    return nodes.findIndex((node) => {
        return node.key == null ? node.value === value : node.key === key;
    });
}

export function nodeIsSelected(key: string, value: string, selectedItems: ITreeItem[]) {
    return findNodeIndex(key, value, selectedItems) > -1;
}

function containsSelectedNode(subNodes: ITreeNode[], selectedItems: ITreeItem[]) {
    if (subNodes != null && selectedItems != null && selectedItems.length > 0) {
        for (let i = 0; i < subNodes.length; i++) {
            const subNode = subNodes[i];

            if (nodeIsSelected(subNode.key, subNode.id, selectedItems)) {
                return true;
            }

            if (containsSelectedNode(subNode.subNodes, selectedItems)) {
                return true;
            }
        }
    }

    return false;
}

function itemIsExpanded(
    expandAll: boolean,
    key: string,
    subNodes: ITreeNode[],
    expandedStates: IExpandedStates,
    selectedNodes: ITreeItem[]
) {
    if (expandAll) {
        return true;
    }

    let result = expandedStates[key];

    if (result == null) {
        result = containsSelectedNode(subNodes, selectedNodes);

        if (result) {
            // When a node is expanded because it is selected, then unselecting it makes its parent collapsed.
            // To fix it, expanded state of the parent is memorized here.
            expandedStates[key] = true;
        }
    }

    return result;
}

function TreeSelectNode(props: {
    itemKey: string;
    id: string;
    title: string;
    focusedItemKey: string;
    references: IItemReferences;
    shownItems: ITreeItem[];
    theme: IValuesCollection;
    checked?: boolean;
    expanded?: boolean;
    allowMultiselection?: boolean;
    subNodesNumber?: number;
    subNodes?: React.JSX.Element[];
    notSelectableParentNodes?: boolean;
    children?: React.ReactNode;
}) {
    props.shownItems.push({
        value: props.id,
        displayValue: props.title,
        key: props.itemKey
    });

    const { theme } = props;
    const itemStyle: React.CSSProperties = {};

    if (theme != null) {
        let background: string;

        if (props.checked) {
            const foregroundColor = theme[themeVars.itemSelector_SelectedItemForegroundColor];

            if (foregroundColor != null) {
                itemStyle.color = foregroundColor;
            }

            background = theme[themeVars.itemSelector_SelectedItemBackgroundColor];
        } else if (props.notSelectableParentNodes && props.subNodesNumber > 0) {
            background = theme[themeVars.itemSelector_InvalidItemBackgroundColor] || 'lightgray';
        }

        if (background != null) {
            itemStyle.background = background;
        }
    }

    return (
        <li
            className={
                'tree-select-node' + (props.checked ? ' active' : '') + (props.subNodesNumber === 0 ? ' leaf' : '')
            }
        >
            <div
                className={
                    'node-item-element' +
                    (props.itemKey === props.focusedItemKey ? ' focused' : '') +
                    (props.expanded ? ' expanded' : '')
                }
                tabIndex={props.shownItems.length} // Setting focus does not work without it.
                ref={(input) => {
                    props.references[props.itemKey] = input;
                }}
                style={itemStyle}
            >
                {props.children}
            </div>

            {props.subNodesNumber > 0 && (
                <ul className="tree-select-nodes-container list-unstyled">{props.subNodes}</ul>
            )}
        </li>
    );
}

export function TreeSelectItem(props: {
    itemKey: string;
    id: string;
    parentID: string;
    title: string;
    item: ITreeNode;
    match?: IMatch;
    searchHighlightingColor?: string;
    references: IItemReferences;
    selectedNodes: ITreeItem[];
    expandedStates: IExpandedStates;
    expandAll: boolean;
    focusedItemKey: string;
    shownItems: ITreeItem[];
    boldParentNodes?: boolean;
    notSelectableParentNodes?: boolean;
    allowMultiselection: boolean;
    theme: IValuesCollection;
    onSelect(node: ITreeItem): void;
    onToggle(treeItem: ITreeItem, item: ITreeNode, expand: boolean): void;
    onCheckedChanged(value: boolean, node: ITreeItem): void;
}) {
    const handleOnSubItemToggle = React.useCallback(
        (treeItem: ITreeItem, item: ITreeNode, expand: boolean) => {
            if (props.onToggle != null) {
                props.onToggle(treeItem, item, expand);
            }
        },
        [props.onToggle]
    );

    const handleOnToggleExpansion = React.useCallback(
        (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
            e.stopPropagation();
            e.preventDefault();

            if (props.onToggle != null) {
                const expanded = props.expandedStates[props.itemKey];

                props.onToggle(
                    { value: props.id, key: props.itemKey, displayValue: props.title },
                    props.item,
                    !expanded
                );
            }
        },
        [props.onToggle, props.expandedStates, props.id, props.itemKey, props.title]
    );

    const { id, title, itemKey, item } = props;
    const checked = nodeIsSelected(itemKey, id, props.selectedNodes);

    const handleOnCheckboxClick = React.useCallback(
        (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
            e.stopPropagation();
            // Not this! - e.preventDefault();
            if (props.onCheckedChanged != null) {
                props.onCheckedChanged(!checked, {
                    value: props.id,
                    displayValue: props.title,
                    key: props.itemKey
                });
            }
        },
        [checked, props.onCheckedChanged, props.id, props.title, props.itemKey]
    );

    const subNodes = item.subNodes != null ? item.subNodes : [];
    const subNodesNumber = subNodes.length;
    const expanded = itemIsExpanded(props.expandAll, itemKey, subNodes, props.expandedStates, props.selectedNodes);

    let subNodesContent: React.JSX.Element[];

    if (expanded) {
        subNodesContent = subNodes.map((node) => {
            return (
                <TreeSelectItem
                    key={node.key}
                    itemKey={node.key}
                    id={node.id}
                    parentID={id}
                    title={node.title}
                    item={node}
                    match={node as ITreeSearchNode}
                    searchHighlightingColor={props.searchHighlightingColor}
                    references={props.references}
                    selectedNodes={props.selectedNodes}
                    focusedItemKey={props.focusedItemKey}
                    boldParentNodes={props.boldParentNodes}
                    notSelectableParentNodes={props.notSelectableParentNodes}
                    shownItems={props.shownItems}
                    expandedStates={props.expandedStates}
                    expandAll={props.expandAll}
                    allowMultiselection={props.allowMultiselection}
                    theme={props.theme}
                    onSelect={props.onSelect}
                    onToggle={handleOnSubItemToggle}
                    onCheckedChanged={props.onCheckedChanged}
                />
            );
        });
    }

    const notSelectableParentNodes = props.notSelectableParentNodes && subNodesNumber > 0;
    const isParentNode = subNodesNumber > 0 || item.subNodesAreNotLoaded;

    const hanleOnTitleClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        if (notSelectableParentNodes && subNodesNumber > 0) {
            handleOnToggleExpansion(e);
        } else if (props.allowMultiselection) {
            handleOnCheckboxClick(e);
        } else if (props.onSelect != null) {
            props.onSelect({ value: id, displayValue: title, key: itemKey });
        }
    };

    let checkboxCell: React.JSX.Element;

    if (props.allowMultiselection && !notSelectableParentNodes) {
        checkboxCell = (
            <td className="checkbox-cell">
                <input
                    className="check-box"
                    type="checkbox"
                    checked={checked}
                    onClick={handleOnCheckboxClick}
                    onChange={() => {}}
                />
            </td>
        );
    }

    let iconCell: React.JSX.Element;

    if (props.item.icon != null) {
        iconCell = (
            <td className="icon-cell" onClick={hanleOnTitleClick}>
                {props.item.icon}
            </td>
        );
    }

    return (
        <TreeSelectNode
            key={itemKey}
            itemKey={itemKey}
            id={id}
            title={title}
            focusedItemKey={props.focusedItemKey}
            checked={checked}
            expanded={expanded}
            allowMultiselection={props.allowMultiselection}
            subNodesNumber={subNodesNumber}
            shownItems={props.shownItems}
            references={props.references}
            subNodes={subNodesContent}
            notSelectableParentNodes={notSelectableParentNodes}
            theme={props.theme}
        >
            <table>
                <tbody>
                    <tr className="node-item-row table-borderless">
                        <TogglerCell
                            collapsed={!expanded}
                            showToggler={isParentNode}
                            onToggle={handleOnToggleExpansion}
                        />

                        {checkboxCell}

                        {iconCell}

                        <td className="title-cell" onClick={hanleOnTitleClick}>
                            <TitleOfMatch
                                match={props.match?.exactMatch != null ? props.match : { begining: title }}
                                highlightingColor={props.searchHighlightingColor}
                                className={
                                    (notSelectableParentNodes ? 'cursor-default' : '') +
                                    (props.boldParentNodes && isParentNode ? ' selected-parent' : '')
                                }
                            />
                        </td>
                    </tr>
                </tbody>
            </table>
        </TreeSelectNode>
    );
}

export function ClearSelectionsItem(props: {
    values: ITreeItem[];
    shownItems: ITreeItem[];
    searchString: string;
    allowNull: boolean;
    focusedItemKey: string;
    references: IItemReferences;
    onClick(): void;
}) {
    const { values, shownItems } = props;
    const clearSelectionsTitle = Globalization.getMessage('clearSelections');

    let numberOfValuesToClear = values != null ? values.length : 0;

    if (numberOfValuesToClear > 0) {
        if (shownItems.length > 0 && props.searchString !== '') {
            values.forEach((value) => {
                if (shownItems.findIndex((match) => match?.value === value?.value) < 0) {
                    if (props.allowNull !== false || numberOfValuesToClear > 1) {
                        numberOfValuesToClear--;
                    }
                }
            });
        } else if (props.allowNull === false && numberOfValuesToClear > 0) {
            numberOfValuesToClear--;
        }
    }

    return (
        <TreeSelectNode
            itemKey={CLEAR_SELECTIONS_VALUE}
            id={CLEAR_SELECTIONS_VALUE}
            title={clearSelectionsTitle}
            focusedItemKey={props.focusedItemKey}
            references={props.references}
            shownItems={shownItems}
            theme={undefined}
        >
            <div className="w-100" onClick={props.onClick}>
                <strong>{clearSelectionsTitle + ` (${numberOfValuesToClear})`}</strong>
            </div>
        </TreeSelectNode>
    );
}

export function ShowMoreItem(props: {
    numberOfHiddenItems: number;
    shownItems: ITreeItem[];
    focusedItemKey: string;
    references: IItemReferences;
    onClick(e): void;
}) {
    return (
        <TreeSelectNode
            itemKey={SHOW_MORE_VALUE}
            id={SHOW_MORE_VALUE}
            title={SHOW_MORE_VALUE}
            focusedItemKey={props.focusedItemKey}
            references={props.references}
            shownItems={props.shownItems}
            theme={undefined}
        >
            <ShowMoreElement
                className="w-100"
                numberOfHiddenItems={props.numberOfHiddenItems}
                onClick={props.onClick}
            />
        </TreeSelectNode>
    );
}
